import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listRunArtifacts, runArtifactUrl, runLog, runProgress, runRecordings, runRecordingUrl, runScreenUrl, runTestVideoUrl,
  type QaCheck, type QaJob, type QaJobRun, type QaLogSource, type QaRecordedTest, type QaRunProgress, type QaRunRef,
} from "../api/qaOrchestrator";
import { formatDuration, parseRunnerLog, phaseDurations, recordingTime, type QaStep, type QaTestTimeline, type QaTimeline } from "../lib/qaTimeline";
import { relativeTime } from "./admin/memberModel";
import { usePolling, type PollContext } from "../hooks/usePolling";

const ACTIVE = new Set(["pending", "dispatching", "queued", "running"]);
const POLL_MS = 2500;
const bytes = (s: string) => new TextEncoder().encode(s).length;

/** The runner log, read once, or followed (only what is new each time) while the run is active. */
function useRunnerLog(ref: QaRunRef, follow: boolean): { text: string; error: string | null } {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const offset = useRef(0);
  const key = `${ref.testRun}/${ref.job}/${ref.run}`;
  useEffect(() => {
    offset.current = 0;
    setText("");
    setError(null);
  }, [key]);
  // One read at a time (usePolling): a slow answer (the runner busy starting or
  // finishing a task) outlived the poll interval, the next read asked for the
  // same offset, and both appended the same lines (steps counted several times).
  const read = useCallback(async ({ signal }: PollContext) => {
    const from = offset.current;
    try {
      const chunk = await runLog(ref, "runner", from || undefined);
      if (signal.aborted || !chunk || offset.current !== from) return;
      offset.current += bytes(chunk);
      setText((t) => t + chunk);
      setError(null);
    } catch (e) {
      // A run that hasn't started yet has no log: not an error worth showing.
      if (!signal.aborted && offset.current === 0) setError((e as Error).message);
    }
  }, [key]);
  usePolling(read, follow ? POLL_MS : null);
  return { text, error };
}

function stepState(s: QaStep | undefined): string {
  return s?.state ?? (s ? "running" : "todo");
}

/** "12/14 steps": of the test's last attempt, how many steps passed, out of all it has. */
function stepSummary(test: QaTestTimeline): string | null {
  const last = test.steps.filter((s) => s.attempt === test.attempts);
  const count = last[0]?.count;
  if (!count) return null;
  const passed = new Set(last.filter((s) => s.state === "pass" || s.state === "captured").map((s) => s.index)).size;
  return `${passed}/${count} steps`;
}

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

/** Where a step begins in its test's own video, if that test has one. */
function stepOffset(video: QaRecordedTest | undefined, s: QaStep): number | null {
  return video?.steps.find((r) => r.index === s.index && r.attempt === s.attempt)?.offset ?? null;
}

/** One test's video, loaded when its row opens; jumps to `seek` (seconds) each time it changes. */
function TestVideo({ refs, file, seek }: { refs: QaRunRef; file: string; seek: { offset: number; n: number } | null }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    runTestVideoUrl(refs, file).then((u) => { made = u; if (live) setUrl(u); else URL.revokeObjectURL(u); }).catch((e) => live && setError((e as Error).message));
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [refs, file]);
  const jump = useCallback(() => {
    const v = video.current;
    if (!v || !seek) return;
    v.currentTime = seek.offset;
    v.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    void v.play().catch(() => {});
  }, [seek]);
  useEffect(() => { if (url) jump(); }, [url, jump]);
  if (error) return <div className="qo-muted qr-video-error">Could not load this test's video: {error}</div>;
  if (!url) return <div className="qr-video-placeholder qr-loading">Loading this test's video…</div>;
  return <video ref={video} className="qr-video" src={url} controls preload="metadata" onLoadedMetadata={jump} />;
}

/** An artifact image, loaded when shown; opens full size in a new tab. */
function ArtifactImage({ refs, name, label }: { refs: QaRunRef; name: string; label: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    runArtifactUrl(refs.project, refs.testRun, refs.job, refs.run, name)
      .then((u) => { made = u; if (live) setUrl(u); else URL.revokeObjectURL(u); })
      .catch(() => live && setFailed(true));
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [refs, name]);
  return (
    <figure className="qr-cmp-fig">
      <figcaption>{label}</figcaption>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={`${label}: ${name}`} /></a>
      ) : (
        <div className="qr-cmp-empty">{failed ? "Not kept" : "Loading…"}</div>
      )}
    </figure>
  );
}

const percent = (r: number) => `${(r * 100).toFixed(r > 0 && r < 0.001 ? 3 : 2)}%`;

/** A screenshot check: its verdict and numbers, and reference | current | difference side by side (open by default when it failed). */
function CheckCompare({ refs, check }: { refs: QaRunRef; check: QaCheck }) {
  const bad = check.kind === "fail" || check.kind === "error";
  const [open, setOpen] = useState(bad);
  const images = [
    check.baseline && { name: check.baseline, label: "Reference" },
    check.artifact && { name: check.artifact, label: "Current" },
    check.diff && { name: check.diff, label: "Difference" },
  ].filter((x): x is { name: string; label: string } => !!x);
  return (
    <div className={`qr-cmp qr-cmp-${check.kind}`}>
      <button className="qr-cmp-head" onClick={() => setOpen((o) => !o)} aria-expanded={open} disabled={images.length === 0}>
        <span className={`qrp-dot qrp-dot-${check.kind}`} aria-hidden="true" />
        <span className="qr-cmp-id">{check.checkId === "default" ? "screenshot" : check.checkId}</span>
        {check.diffRatio !== undefined && (
          <span className="qo-muted">
            {percent(check.diffRatio)} different{check.tolerance !== undefined ? ` · allowed ${percent(check.tolerance)}` : ""}
          </span>
        )}
        {check.kind === "captured" && <span className="qo-muted">captured as a new reference</span>}
        {images.length > 0 && <span className="qr-cmp-toggle">{open ? "Hide" : "Compare"}</span>}
      </button>
      {check.reason && bad && <div className="qr-step-reason qr-cmp-reason">{check.reason}</div>}
      {open && images.length > 0 && (
        <div className="qr-cmp-images">
          {images.map((i) => <ArtifactImage key={i.name} refs={refs} name={i.name} label={i.label} />)}
        </div>
      )}
    </div>
  );
}

/** The checks a step ran: the latest report of each, from between the step's start and the next step's. */
function stepChecks(checks: QaCheck[], testId: string, step: QaStep, next: QaStep | undefined): QaCheck[] {
  const mine = checks.filter((c) => {
    if (c.testId !== testId || c.stepIndex !== step.index) return false;
    const at = Date.parse(c.at);
    return at >= step.at - 1000 && (!next || at <= next.at + 1000);
  });
  return [...new Map(mine.map((c) => [c.checkId, c])).values()];
}

/** A test in the plan's list: verdict, name, its steps as squares, how long it took. */
function TestItem({ test, selected, onSelect }: { test: QaTestTimeline; selected: boolean; onSelect: () => void }) {
  const status = test.status ?? "running";
  const duration = test.end !== undefined ? test.end - test.at : null;
  return (
    <button className={`qr-test qr-test-${status}${selected ? " on" : ""}`} onClick={onSelect} aria-current={selected ? "true" : undefined}>
      <span className="qr-test-top">
        <span className={`qrp-dot qrp-dot-${status}`} aria-hidden="true" />
        <strong className="qr-test-id">{test.id}</strong>
        <span className="qo-muted qr-test-meta">
          {stepSummary(test) ? `${stepSummary(test)} · ` : ""}
          {test.attempts > 1 ? `${test.attempts} tries · ` : ""}
          {duration !== null ? formatDuration(duration) : "…"}
        </span>
      </span>
    </button>
  );
}

/** The chosen test: where it failed and why, its video, screenshots and every step. */
function TestDetail({ refs, test, onSeek, canSeek, shots, video, seek, checks }: {
  refs: QaRunRef;
  test: QaTestTimeline;
  /** The run's screenshot checks (all tests'); none for a run from before they were reported. */
  checks: QaCheck[];
  onSeek: (s: QaStep) => void;
  canSeek: boolean;
  shots: { name: string; url: string }[];
  /** The test's own video (a per-test recording); absent for a run recorded as one video. */
  video?: QaRecordedTest;
  seek: { offset: number; n: number } | null;
}) {
  const status = test.status ?? "running";
  const duration = test.end !== undefined ? test.end - test.at : null;
  // Steps whose details (what it does, what it saw) are shown: clicked open one by one.
  const [opened, setOpened] = useState<ReadonlySet<number>>(new Set());
  useEffect(() => setOpened(new Set()), [test.id]);
  const toggle = (i: number) => setOpened((o) => {
    const next = new Set(o);
    if (!next.delete(i)) next.add(i);
    return next;
  });
  const anchor = (s: QaStep) => {
    const offset = stepOffset(video, s);
    if (video && offset === null) return null;
    return (
      <button className="qr-seek qr-seek-small" onClick={() => onSeek(s)} title="Play the video from this step">
        ▶{offset !== null ? ` ${clock(offset)}` : ""}
      </button>
    );
  };
  return (
    <div className={`qr-detail qr-detail-${status}`}>
      <div className="qr-detail-head">
        <span className={`qo-pill qo-pill-${status}`}>{status}</span>
        <strong className="qr-test-id">{test.id}</strong>
        <span className="qo-muted">
          {test.steps.filter((s) => s.attempt === test.attempts).length} steps
          {test.attempts > 1 ? ` · ${test.attempts} attempts` : ""}
          {duration !== null ? ` · ${formatDuration(duration)}` : ""}
        </span>
      </div>
      {test.failed && (
        <div className="qr-failure">
          <span className="qr-failure-where">
            Failed at step {test.failed.index + 1}/{test.failed.count} · <code>{test.failed.action}</code>
          </span>
          {test.failed.reason && <span className="qr-failure-reason">{test.failed.reason}</span>}
          {canSeek && <button className="qr-seek" onClick={() => onSeek(test.failed!)}>▶ Watch this step</button>}
        </div>
      )}
      {video && <TestVideo refs={refs} file={video.file} seek={seek} />}
      {shots.length > 0 && !checks.some((c) => c.testId === test.id && c.baseline) && (
        <div className="qr-detail-section">
          <h3>Screenshots</h3>
          <div className="qo-shots">
            {shots.map((s) => (
              <figure key={s.name}>
                <a href={s.url} target="_blank" rel="noreferrer"><img src={s.url} alt={s.name} /></a>
                <figcaption>{s.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
      <div className="qr-detail-section">
        <h3>Steps</h3>
        <ol className="qr-steps">
          {test.steps.map((s, i) => (
            <li key={i} className={`qr-step qr-step-${stepState(s)}`}>
              {s.detail || s.seen ? (
                <button className="qr-step-head" onClick={() => toggle(i)} aria-expanded={opened.has(i)} title="Show what this step does">
                  <span className="qr-step-dot" aria-hidden="true" />
                  <span className="qr-step-n">{s.index + 1}/{s.count}</span>
                  <code>{s.action}</code>
                </button>
              ) : (
                <span className="qr-step-head">
                  <span className="qr-step-dot" aria-hidden="true" />
                  <span className="qr-step-n">{s.index + 1}/{s.count}</span>
                  <code>{s.action}</code>
                </span>
              )}
              {s.attempt > 1 && <span className="qo-tag">attempt {s.attempt}</span>}
              <span className="qr-step-state">{stepState(s)}</span>
              {s.ms !== undefined && <span className="qo-muted">{formatDuration(s.ms)}</span>}
              {canSeek && anchor(s)}
              {opened.has(i) && s.detail && <div className="qr-step-detail">{s.detail}</div>}
              {opened.has(i) && s.seen && <div className="qr-step-seen">saw: {s.seen}</div>}
              {s.reason && <div className="qr-step-reason">{s.reason}</div>}
              {s.action === "screenshot" && (() => {
                const mine = stepChecks(checks, test.id, s, test.steps[i + 1]);
                return mine.length > 0 && <div className="qr-step-checks">{mine.map((c) => <CheckCompare key={c.checkId} refs={refs} check={c} />)}</div>;
              })()}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

/** A run recorded as one video (before per-test videos): loaded on demand, then jumps to `seek` (wall-clock ms) whenever it changes. */
function Recording({ refs, timeline, seek, requested, onRequest }: {
  refs: QaRunRef;
  timeline: QaTimeline;
  seek: { at: number; n: number } | null;
  requested: boolean;
  onRequest: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (!requested || url) return;
    let live = true;
    let made: string | null = null;
    runRecordingUrl(refs).then((u) => { made = u; if (live) setUrl(u); else URL.revokeObjectURL(u); }).catch((e) => live && setError((e as HttpLike).status === 404 ? "This run has no recording (it stopped before the plan's steps began, or the runner no longer keeps it)." : (e as Error).message));
    return () => { live = false; if (made && !url) URL.revokeObjectURL(made); };
  }, [requested]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const jump = useCallback(() => {
    const v = video.current;
    if (!v || !seek) return;
    const t = recordingTime(timeline, seek.at, v.duration);
    if (t === null) return;
    v.currentTime = t;
    v.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    void v.play().catch(() => {});
  }, [seek, timeline]);
  useEffect(() => { if (url) jump(); }, [seek, url, jump]);

  if (!requested) {
    return (
      <button className="qr-video-placeholder" onClick={onRequest}>
        <span className="qr-play" aria-hidden="true">▶</span>
        <span>Watch the recording</span>
        {timeline.recording && <span className="qo-muted">{formatDuration(timeline.recording.end - timeline.recording.start)} of the plan's steps</span>}
      </button>
    );
  }
  if (error) return <div className="qo-muted qr-video-error">{error}</div>;
  if (!url) return <div className="qr-video-placeholder qr-loading">Loading the recording…</div>;
  return <video ref={video} className="qr-video" src={url} controls preload="metadata" onLoadedMetadata={jump} />;
}
type HttpLike = { status?: number };

/** What the runner says about the task right now, polled; `unreachableSince` once a poll fails (the last answer is then stale). */
type LiveState = { progress: QaRunProgress | null; frame: string | null; unreachableSince: number | null };

function useLive(refs: QaRunRef, enabled: boolean): LiveState {
  const [state, setState] = useState<LiveState>({ progress: null, frame: null, unreachableSince: null });
  const shown = useRef<string | null>(null);
  useEffect(() => () => {
    if (shown.current) URL.revokeObjectURL(shown.current);
    shown.current = null;
  }, [refs.testRun, refs.job, refs.run, enabled]);
  const tick = useCallback(async ({ signal, resumed }: PollContext) => {
    const p = await runProgress(refs).catch(() => null);
    if (signal.aborted) return;
    if (!p) {
      // After a hidden tab's pause the old failure says nothing about now: the
      // clock starts again, unless the runner was already offline before.
      setState((s) => ({ ...s, unreachableSince: s.unreachableSince === null || (resumed && !offline(s)) ? Date.now() : s.unreachableSince }));
      return;
    }
    // The screen only while the runner is really running the task.
    const f = p.status === "running" ? await runScreenUrl(refs).catch(() => null) : null;
    if (signal.aborted) { if (f) URL.revokeObjectURL(f); return; }
    if (f) {
      if (shown.current) URL.revokeObjectURL(shown.current);
      shown.current = f;
    }
    setState((s) => ({ progress: p, frame: p.status === "running" ? (f ?? s.frame) : null, unreachableSince: null }));
  }, [refs.testRun, refs.job, refs.run]);
  usePolling(tick, POLL_MS, { enabled });
  return state;
}

/**
 * How long the runner must keep failing before it counts as offline. A task
 * starting or finishing keeps the runner's disk busy, and a single answer can
 * then take over 15 s or time out on the way; that is not the runner being gone.
 */
export const OFFLINE_AFTER_MS = 20_000;

const offline = (live: LiveState, now = Date.now()) => live.unreachableSince !== null && now - live.unreachableSince >= OFFLINE_AFTER_MS;

/** The plan run's status as the runner sees it now: back in its queue after a restart, or not answering at all. */
function liveStatus(runStatus: string, live: LiveState): string {
  if (!ACTIVE.has(runStatus)) return runStatus;
  if (offline(live)) return "offline";
  if (live.progress?.status === "queued") return "queued";
  return runStatus;
}

/** What the runner is doing right now: phase, test and step, and its screen. */
function LiveView({ timeline, live }: { timeline: QaTimeline; live: LiveState }) {
  const { progress, frame } = live;
  // A brief failure keeps showing the last answer; only a lasting one shows "not answering".
  const unreachableSince = offline(live) ? live.unreachableSince : null;
  const p = progress?.progress;
  const running = unreachableSince === null && progress?.status === "running";
  const done = timeline.tests.filter((t) => t.status).length;
  const total = p?.test?.count ?? null;
  const phase = timeline.phases.at(-1);
  const badge = unreachableSince !== null ? "NO SIGNAL" : progress?.status === "queued" ? "QUEUED" : "LIVE";
  return (
    <div className="qr-live">
      <div className="qr-live-screen">
        {frame && running ? <img src={frame} alt="The runner's screen, live" /> : <span className="qo-muted qr-live-wait">{unreachableSince !== null ? "The runner isn't answering." : progress?.status === "queued" ? "Not started yet." : "Waiting for the test setup…"}</span>}
        <span className={`qr-live-badge${running ? "" : " qr-live-badge-off"}`}>{running && <span className="qr-live-dot" />}{badge}</span>
      </div>
      <div className="qr-live-info">
        {unreachableSince !== null ? (
          <>
            <div className="qr-live-label">The runner isn't answering</div>
            <div className="qo-muted">No answer for {formatDuration(Date.now() - unreachableSince)}. It may be restarting or turned off; this updates by itself once it's back.</div>
          </>
        ) : progress?.status === "queued" ? (
          <>
            <div className="qr-live-label">{progress.label ?? "Queued on the runner"}</div>
            <div className="qo-muted">{progress.phase === "waiting_for_idle" ? "The runner starts a test only once its desktop has had no keyboard or mouse input for 10 minutes." : "It starts when the tasks before it are done."}</div>
          </>
        ) : (
          <>
            <div className="qr-live-label">{progress?.label ?? p?.phaseDetail ?? "Waiting for the runner…"}</div>
            {p?.activity && <div className="qo-muted">{p.activity}</div>}
            {total !== null && (
              <div className="qr-progress" title={`${done} of ${total} tests done`}>
                <span style={{ width: `${Math.min(100, (done / total) * 100)}%` }} />
              </div>
            )}
            {total !== null && <div className="qo-muted">{done} of {total} tests done{p?.step ? ` · now: ${p.step.label}` : ""}</div>}
            {running && phase && <div className="qo-muted">{phase.label} · since {formatDuration(Date.now() - phase.at)}</div>}
          </>
        )}
      </div>
    </div>
  );
}

const LOG_TABS: { source: QaLogSource; label: string; hint: string }[] = [
  { source: "runner", label: "Runner", hint: "the harness: phases, steps, verdicts" },
  { source: "server", label: "Server", hint: "the Bedrock server console: world setup, the pack's script errors" },
  { source: "client", label: "Client", hint: "the Minecraft client's own log: resource pack and UI errors" },
];

function Logs({ refs, runnerText, follow }: { refs: QaRunRef; runnerText: string; follow: boolean }) {
  const [source, setSource] = useState<QaLogSource>("runner");
  const [other, setOther] = useState<Partial<Record<QaLogSource, string>>>({});
  const [filter, setFilter] = useState("");
  const [problems, setProblems] = useState(false);
  const pre = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (source === "runner" || (other[source] !== undefined && !follow)) return;
    let live = true;
    runLog(refs, source).then((t) => live && setOther((o) => ({ ...o, [source]: t }))).catch((e) => live && setOther((o) => ({ ...o, [source]: `(${(e as Error).message})` })));
    return () => { live = false; };
  }, [source]);
  const text = source === "runner" ? runnerText : other[source];
  const lines = useMemo(() => {
    const all = (text ?? "").split(/\r?\n/).filter(Boolean);
    const f = filter.trim().toLowerCase();
    return all.filter((l) => (!f || l.toLowerCase().includes(f)) && (!problems || /error|fail|warn|exception/i.test(l)));
  }, [text, filter, problems]);
  // Following: stay at the bottom as lines arrive.
  useEffect(() => {
    const el = pre.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [lines.length, follow]);
  return (
    <div className="qr-logs">
      <div className="qr-logs-head">
        {LOG_TABS.map((t) => (
          <button key={t.source} className={source === t.source ? "on" : ""} onClick={() => setSource(t.source)} title={t.hint}>{t.label}</button>
        ))}
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter lines" aria-label="Filter log lines" />
        <label className="qr-logs-check"><input type="checkbox" checked={problems} onChange={(e) => setProblems(e.target.checked)} /> Problems only</label>
      </div>
      <pre ref={pre} className="qr-log">
        {text === undefined ? "Loading…" : lines.length === 0 ? "(nothing)" : lines.map((l, i) => (
          <div key={i} className={/ -> (error|fail)|\bERROR\b|\[test\] .* -> (error|fail)/.test(l) ? "qr-log-bad" : /\[phase\]|\[test\]/.test(l) ? "qr-log-mark" : undefined}>{l}</div>
        ))}
      </pre>
    </div>
  );
}

/** Screenshots of failed checks, per test id (their names start with it). */
function useShots(refs: QaRunRef, enabled: boolean): { name: string; url: string }[] {
  const [shots, setShots] = useState<{ name: string; url: string }[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    let urls: string[] = [];
    (async () => {
      const names = (await listRunArtifacts(refs.project, refs.testRun, refs.job, refs.run)).filter((n) => /\.(png|jpe?g)$/i.test(n)).slice(0, 24);
      const loaded = await Promise.all(names.map(async (name) => ({ name, url: await runArtifactUrl(refs.project, refs.testRun, refs.job, refs.run, name) })));
      urls = loaded.map((s) => s.url);
      if (live) setShots(loaded);
    })().catch(() => {});
    return () => { live = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [enabled, refs.run]);
  return shots;
}

type Tab = "tests" | "logs";

/** One plan's run on one computer: live while it runs; afterwards its timeline, the recording, screenshots and logs. */
export function QaPlanRun({ project, testRun, job, run }: { project: string; testRun: string; job: QaJob; run: QaJobRun }) {
  const refs = useMemo(() => ({ project, testRun, job: job.id, run: run.id }), [project, testRun, job.id, run.id]);
  const active = ACTIVE.has(run.status);
  // A finished run always has something to show; an active one only once the runner has begun it.
  const started = !active || run.status === "running";
  const { text, error } = useRunnerLog(refs, active);
  // Once the run is on a runner, what the runner says now wins over the orchestrator's last word.
  const live = useLive(refs, active && run.computerId !== null && run.status !== "dispatching");
  const shownStatus = liveStatus(run.status, live);
  // The screenshot checks: live while running, the runner's saved report once done.
  const [savedChecks, setSavedChecks] = useState<QaCheck[]>([]);
  useEffect(() => {
    if (active) return;
    let on = true;
    runProgress(refs).then((p) => on && setSavedChecks(p.progress?.checks ?? [])).catch(() => {});
    return () => { on = false; };
  }, [refs, active]);
  const checks = active ? live.progress?.progress?.checks ?? [] : savedChecks;
  const timeline = useMemo(() => parseRunnerLog(text), [text]);
  const [tab, setTab] = useState<Tab>("tests");
  // The test shown beside the list; until one is picked, the first failing one (or the one running).
  const [picked, setPicked] = useState<string | null>(null);
  const [watch, setWatch] = useState(false);
  const [seek, setSeek] = useState<{ at: number; n: number } | null>(null);
  // One video per test (undefined while loading; null: the run was recorded as one video).
  const [videos, setVideos] = useState<QaRecordedTest[] | null | undefined>(undefined);
  const [testSeek, setTestSeek] = useState<{ testId: string; offset: number; n: number } | null>(null);
  useEffect(() => {
    if (active) return;
    let live = true;
    runRecordings(refs).then((v) => live && setVideos(v)).catch(() => live && setVideos(null));
    return () => { live = false; };
  }, [refs, active]);
  const perTest = videos != null && videos.length > 0;
  const failedRun = run.status === "fail" || run.status === "error" || run.status === "captured";
  const shots = useShots(refs, !active && failedRun);

  // From the runner's result when the log has nothing to say (a run from before logs, or one that never started).
  const fallback = run.result?.results ?? [];
  const tests = timeline.tests.length > 0 ? timeline.tests : [];
  const counts = tests.reduce<Record<string, number>>((m, t) => { const k = t.status ?? "running"; m[k] = (m[k] ?? 0) + 1; return m; }, {});
  const setup = phaseDurations(timeline).filter((p) => !p.label.startsWith("running the plan") && !p.label.startsWith("finished"));
  const planPhase = timeline.recording;
  const totalMs = timeline.start !== null && timeline.end !== null ? timeline.end - timeline.start : null;
  // The plan run's time in three: getting a server and client ready, the tests themselves, and wrapping up (videos).
  const split = planPhase && timeline.start !== null && timeline.end !== null
    ? [
        { label: "Setup", ms: planPhase.start - timeline.start },
        { label: "Tests", ms: planPhase.end - planPhase.start },
        { label: "Wrap-up", ms: timeline.end - planPhase.end },
      ]
    : null;

  const doSeek = (testId: string, step: QaStep) => {
    if (perTest) {
      const video = videos!.find((v) => v.testId === testId);
      const offset = video ? stepOffset(video, step) : null;
      if (offset === null) return;
      setPicked(testId);
      setTestSeek((s) => ({ testId, offset, n: (s?.n ?? 0) + 1 }));
      return;
    }
    setWatch(true);
    setSeek((s) => ({ at: step.at, n: (s?.n ?? 0) + 1 }));
  };
  const current = tests.find((t) => t.id === picked)
    ?? tests.find((t) => t.status === "fail" || t.status === "error")
    ?? tests.find((t) => !t.status)
    ?? tests[0];
  const shotsOf = (id: string) => shots.filter((s) => s.name.startsWith(id));
  const canSeekTest = (id: string) => !active && (perTest ? videos!.some((v) => v.testId === id) : videos === null && timeline.recording !== null);

  return (
    <div className={`qr-run qr-run-${shownStatus}`}>
      <div className="qr-run-head">
        <span className={`qo-pill qo-pill-${shownStatus}`}>{shownStatus}</span>
        <strong>{job.plan ?? job.id}</strong>
        {run.computerName && <span className="qo-muted">on {run.computerName}</span>}
        {totalMs !== null && !active && <span className="qo-muted">· {formatDuration(totalMs)}</span>}
        {run.finishedAt && <span className="qo-muted">· {relativeTime(run.finishedAt)}</span>}
        <span className="qr-counts">
          {Object.entries(counts).map(([k, v]) => <span key={k} className={`qr-count qr-count-${k}`}>{v} {k}</span>)}
        </span>
      </div>

      {(run.error || run.result?.error) && <div className="qr-run-error">{run.result?.error ?? run.error}</div>}

      {active && run.computerId === null && <div className="qo-muted">Waiting for a runner…</div>}
      {active && run.computerId !== null && <LiveView timeline={timeline} live={live} />}

      {!active && started && (
        <>
          {split && (
            <details className="qr-time">
              <summary title="Where the run's time went">
                {split.map((p) => <span key={p.label} className="qr-time-part">{p.label} <b>{formatDuration(p.ms)}</b></span>)}
              </summary>
              <ol className="qr-phases">
                {setup.map((p, i) => (
                  <li key={i} className="qr-phase">{p.label}{p.ms !== null ? <b>{formatDuration(p.ms)}</b> : null}</li>
                ))}
              </ol>
            </details>
          )}
          {videos === null && <Recording refs={refs} timeline={timeline} seek={seek} requested={watch} onRequest={() => setWatch(true)} />}
        </>
      )}

      {started && (
        <div className="qr-tabs">
          <button className={tab === "tests" ? "on" : ""} onClick={() => setTab("tests")}>Tests{tests.length ? ` (${tests.length})` : ""}</button>
          <button className={tab === "logs" ? "on" : ""} onClick={() => setTab("logs")}>Logs</button>
        </div>
      )}

      {started && tab === "tests" && (
        <div className="qr-tests">
          {tests.length > 0 && current ? (
            <div className="qr-split">
              <div className="qr-test-list" role="list" aria-label="Tests">
                {tests.map((t) => <TestItem key={t.id} test={t} selected={t.id === current.id} onSelect={() => setPicked(t.id)} />)}
              </div>
              <TestDetail
                key={current.id}
                refs={refs}
                test={current}
                onSeek={(step) => doSeek(current.id, step)}
                canSeek={canSeekTest(current.id)}
                shots={shotsOf(current.id)}
                video={perTest ? videos!.find((v) => v.testId === current.id) : undefined}
                seek={testSeek?.testId === current.id ? testSeek : null}
                checks={checks}
              />
            </div>
          ) : fallback.length > 0 ? (
            <ul className="qo-outcomes">
              {fallback.map((o) => <li key={o.testId}>{o.testId} <span>{o.outcome.kind}{o.outcome.reason ? `: ${o.outcome.reason}` : ""}</span></li>)}
            </ul>
          ) : (
            <div className="qo-muted">{error ? `No step log: ${error}` : active ? "The plan's steps haven't started yet." : "No tests ran."}</div>
          )}
          {shots.filter((s) => !tests.some((t) => s.name.startsWith(t.id))).length > 0 && (
            <div className="qo-shots">
              {shots.filter((s) => !tests.some((t) => s.name.startsWith(t.id))).map((s) => (
                <figure key={s.name}><a href={s.url} target="_blank" rel="noreferrer"><img src={s.url} alt={s.name} /></a><figcaption>{s.name}</figcaption></figure>
              ))}
            </div>
          )}
        </div>
      )}

      {started && tab === "logs" && <Logs refs={refs} runnerText={text} follow={active} />}
    </div>
  );
}
